import "core-js/modules/es.date.to-string.js";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/**
 * utils.js
 *
 * Utility functions useful in more than one spot. Should be accessible anywhere.
 */


import base64 from 'base-64';
import QueryString from 'qs';
/**
 * Provide a BDC Image Resizer friendly, HTTPS path for an image. This function will
 * base-64 encode the passed URL and assemble the new one. If the original image is an SVG
 * this will return the URL unchanged. Typical uses include:
 * - in an ImageResizer component
 * - as the src attribute for a traditional <img /> element
 * - as the value of an inline style declaration, e.g. backroundImage
 *
 * @param  {String} options.src - URL to original image
 * @param  {String} options.method Image resizer method. Possible values are "w", "r", and "rc"
 * @param  {Number} options.w Width in pixels to resize to
 * @param  {Number} options.h Height in pixels to resize to (optional)
 *
 * @return {String} Protocol-agnostic URL to BDC image resizer asset or default placeholder
 */

export function makeImgResizerUrl(_ref) {
  var src = _ref.src,
      _ref$method = _ref.method,
      method = _ref$method === void 0 ? null : _ref$method,
      _ref$w = _ref.w,
      w = _ref$w === void 0 ? null : _ref$w,
      h = _ref.h; // return original if an SVG

  if (/.svg$/.test(src)) {
    return src;
  }

  if (src === undefined || src === null || src === '' || /images\/$/.test(src)) {
    src = 'https://www.business.com/images/bdc-placeholder.jpg';
  }

  if (/^\/images\/*/.test(src)) {
    src = "".concat(process.env.NODE_ENV === 'production' ? "https://www.business.com" : "https://staging.business.com").concat(src);
  } // Error if there is a bad method passed


  if (!/w|h|r|rc/.test(method)) {
    throw new Error('Bad method passed to image resizer');
  } // Error if height is missing when needed


  if (/rc?/.test(method) && !h) {
    throw new Error('Cannot resize image without height');
  } // Error if non-numeric dimensions passed


  if (!/\d/.test(w | h) || /rc?/.test(method) && !/\d/.test(h)) {
    throw new Error('Non-numeric dimension(s) passed to image resizer');
  }

  var dims = w ? "".concat(w).concat(h ? "x".concat(h) : '') : h;
  return "https://img.business.com/".concat(method, "/").concat(dims, "/").concat(base64.encode(src));
}
/**
 * Provide a BDC Image Resizer friendly, protocol-agnostic path for an image. This function will
 * base-64 encode the passed URL and assemble a BLURRED VERSION. Typical uses include:
 * - in an ImageResizer component
 * - as the src attribute for a traditional <img /> element
 * - as the value of an inline style declaration, e.g. backroundImage
 *
 * @param  {String} options.src - URL to original image
 * @param  {Number} options.radius - Number between 0 and 10 (the smaller the better) (https://en.wikipedia.org/wiki/Gaussian_blur)
 * @param  {Number} options.signam - How large of a radius to add blur (https://en.wikipedia.org/wiki/Gaussian_blur)
 * @param  {Number} options.w - Width in pixels to resize to (optional)
 * @param  {Number} options.h - Height in pixels to resize to (optional)
 *
 * @return {String} Protocol-agnostic URL to BDC image resizer asset or default placeholder
 */

export function makeImgResizeBlurUrl(_ref2) {
  var src = _ref2.src,
      _ref2$radius = _ref2.radius,
      radius = _ref2$radius === void 0 ? 1 : _ref2$radius,
      _ref2$sigma = _ref2.sigma,
      sigma = _ref2$sigma === void 0 ? 1 : _ref2$sigma,
      w = _ref2.w,
      h = _ref2.h; // Error if non-numeric dimensions passed

  if (src === null || src === '' || src === '/images/' || src === 'https://www.business.com/images/') {
    src = 'https://www.business.com/images/bdc-placeholder.jpg';
  }

  if (!/\d/.test(radius) && !/\d/.test(sigma)) {
    throw new Error('Non-numeric dimension(s) passed to image blurrer');
  }

  var dims = "".concat(radius, "x").concat(sigma);
  return "https://img.business.com/custom/blur=".concat(dims).concat(w ? ",dw=".concat(w) : '').concat(h ? ",dh=".concat(h) : '', "/").concat(base64.encode(src));
}
/**
 * Takes a url path and tests against 2 regexes to determine if it is an
 * external or internal link
 *
 * @param  {String} url :: The url to be tested against regexes
 *
 * @return {Boolean}
 */

export var isExternalUrl = function isExternalUrl(url) {
  // @todo need to add a check against domain instead of regex
  var commonProtocols = new RegExp(/^https?:\/\//);
  var localCanonicalUrls = new RegExp(/^https?:\/\/((dev|qa|bcs|staging|www)*\.)?(business|businessnewsdaily)\.com\/?.*|^http:\/\/us-east1-dev-bcs-web1*|^http:\/\/127.0.0.1/); // a link is external if it has protocols and DOES NOT match one of the bcs internal canonical urls

  return commonProtocols.test(url) && !localCanonicalUrls.test(url);
};
/**
 * Takes a url path and returns an object defining what the the rel and target
 * values should be based on whether a url is external/internal
 *
 * @param  {String} url :: The url to be tested against regexes
 *
 * @return {Object} :: The value of link attributes
 */

export var getAdditionalLinkAttributes = function getAdditionalLinkAttributes(url) {
  return !isExternalUrl(url) ? {} : {
    rel: 'nofollow noopener noreferrer',
    target: '_blank'
  };
};
/**
 * Promise chain helper function to check status
 * Assumes interacting with JSON response
 */

export var handleResponse = function handleResponse(response) {
  return response.json().then(function (json) {
    if (response.ok && response.status >= 200 && response.status < 300) {
      return json;
    } else {
      return Promise.reject(_objectSpread(_objectSpread({}, json), {}, {
        status: response.status,
        statusText: response.statusText
      }));
    }
  });
}; // This is an agnostic function that's used to send AJAX requests to either
// external APIs or the Symfony tier that runs BCS

export var loadData = function loadData(endpoint) {
  var init = {
    method: 'GET',
    headers: {
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=-1',
      'X-Requested-With': 'XMLHttpRequest'
    }
  };
  return fetch(endpoint, init).then(handleResponse)["catch"](function (ex) {
    console.error("Portals loadData module -- ".concat(ex.status, " error: ").concat(ex.statusText));
  });
};
/**
 * Try to smoothly scroll to an element on DOM. On failure just scrollTo without any behavior type
 * Some mobile browsers don't support object signature.
 *
 * @param {object} element - the DOM node to scroll to
 */

export function smoothScrollIntoView(element) {
  try {
    element.scrollIntoView({
      behavior: 'smooth'
    });
  } catch (ex) {
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView();
    }
  }
}
/**
 * splits an incoming HTML string into an array of paragraphs, split on <p> tags. Also scrapes the string
 * for any double linebreak tags to replace those with paragraphs first.
 *
 * @param  {String} paragraphString :: Raw HTML string to be split
 *
 * @return {Array} of parahraph strings. Each string *assumed* to start with a "<p>" and have no trailing "</p>"
 */

export function splitParagraphString(paragraphString) {
  return paragraphString.trim().replace(/\n(\s*\n)+/g, '</p><p>').split('</p>').map(function (e) {
    return "".concat(e, "</p>");
  });
}
/**
 * Takes a passed array of strings that have been split on "</p>" and returns all paragraphs between "start" and
 * "end", properly formatted with wrapping "p" tags
 *
 * @param  {Array} paragraphs :: Each string *assumed* to start with a "<p>" and have no trailing "</p>"
 * @param  {Number} start :: 0-based index of where to start slicing "paragrahs" array
 * @param  {Number} end :: 0-based index of where to end slicing of "paragraphs" array
 *
 * @return {String} New HTML string with desired paragraphs, each wrapped in a "<p>""
 */

export var getParagraphSections = function getParagraphSections(paragraphs, start, end) {
  var copy = paragraphs.slice(start, end);
  copy.push('');
  return copy.join('</p>').replace(/<\/p><\/p>/g, '</p>');
};
/**
 * function that returns the date and time in the required format for BI(2018-05-24 12:30:00).
 *
 * @param {Object} :: It contains all the required format values for the date to be returned.
 * @return {object} :: date returned which is built on values which are sent.
 *
 */

export function formatDate() {
  var d = new Date();
  var month = d.getMonth() + 1 > 9 ? d.getMonth() + 1 : "0".concat(d.getMonth() + 1);
  var date = d.getDate() > 9 ? d.getDate() : "0".concat(d.getDate());
  var hours = d.getHours() > 9 ? d.getHours() : "0".concat(d.getHours());
  var minutes = d.getMinutes() > 9 ? d.getMinutes() : "0".concat(d.getMinutes());
  var seconds = d.getSeconds() > 9 ? d.getSeconds() : "0".concat(d.getSeconds());
  return "".concat(d.getFullYear(), "-").concat(month, "-").concat(date, " ").concat(hours, ":").concat(minutes, ":").concat(seconds);
}
/** Converts an array of object (with multiple child objects) to array of objects
 * For example convertArrOfObjToArrOfObjs([{1: {val: 1}, 2: {val:2}}], 'id') --> [{id: 1, val: 1}, {id:2, val:2}]
 *
 * @param  {Array} dataArr :: An array containing a single object
 * @param  {string} [strippedKeyName = 'id'] :: The name of the stripped key (default value is 'id')
 *
 * @return {Array.<Object>} Returns an array of objects with the stripped key inside the child object
 */

export var convertArrOfObjToArrOfObjs = function convertArrOfObjToArrOfObjs(dataArr) {
  var strippedKeyName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'id';

  if (!dataArr || dataArr.length === 0) {
    return []; // return empty array
  }

  var arrayOfObjects = dataArr.reduce(function (accumulator, dataObj) {
    var reducedObjs = Object.keys(dataObj).reduce(function (acc, key) {
      var currentObject = dataObj[key];
      return [].concat(_toConsumableArray(acc), [_objectSpread(_objectSpread({}, currentObject), {}, _defineProperty({}, strippedKeyName, key))]);
    }, []);
    return reducedObjs;
  }, []);
  return arrayOfObjects;
};
/**
 * Fetch user latitude and longitude using Geolocation.
 * @returns {object} Returns an object with the keys "lat" and "lng" (default value)
 */

export function getLatAndLongByGeolocation() {
  // default object is center of the country to ensure that values are always defined
  var DEFAULT_LAT_LONG_OBJ = {
    lat: 37.751,
    lng: -97.822
  };
  var apiKey = 'AIzaSyA5rEknjX0KqxBmVafC10L4ctWbBoSEIyE'; // Geolocation Key used with call connect

  return fetch("https://www.googleapis.com/geolocation/v1/geolocate?key=".concat(apiKey), {
    method: 'POST'
  }).then(handleResponse).then(function (coordinates) {
    var _coordinates$location = coordinates.location,
        lat = _coordinates$location.lat,
        lng = _coordinates$location.lng;

    if (lat && lng) {
      return {
        lat: lat,
        lng: lng
      };
    } else {
      return DEFAULT_LAT_LONG_OBJ;
    }
  })["catch"](function (ex) {
    console.error(ex);
    return DEFAULT_LAT_LONG_OBJ;
  });
}
/**
 * Truncate function that takes a string and checks the length
 * @param {string} text the text you want to check
 * @param {number} charLimit the character limit you want to check against
 *
 * @return {string} that appends ... if the length is greater than character limit
 */

export function truncateString(text, charLimit) {
  if (typeof text === 'undefined') {
    return '';
  }

  if (text.length <= charLimit) {
    return text;
  } else {
    var truncatedText = text.substring(0, charLimit);
    return "".concat(truncatedText.substr(0, Math.min(truncatedText.length, truncatedText.lastIndexOf(' '))), "...");
  }
}
export function isBrowser() {
  return typeof window !== "undefined";
}
export function handleModalCloseButton(modalVisible, setModalVisible) {
  if (modalVisible) {
    setModalVisible(false);
  } else {
    window.location = '/';
  }
}
export var isPastScrollYMark = function isPastScrollYMark(percentage, setScroll) {
  if (!isBrowser()) {
    return;
  } // handle various cases in different browsers, devices, and so on.


  var yScrollMark = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight, document.documentElement.offsetHeight, document.body.clientHeight, document.documentElement.clientHeight) * percentage;

  var handleScroll = function handleScroll() {
    return setScroll(window.pageYOffset >= yScrollMark);
  };

  window.addEventListener('scroll', handleScroll);
  return function () {
    window.removeEventListener('scroll', handleScroll);
  };
};
export function replaceSpacesWithDashes(text) {
  return text.replace(/\s/g, '-');
}
export function flattenArray(array) {
  return array.reduce(function (accumulator, currentValue) {
    return accumulator.concat(currentValue);
  }, []);
}
export function generateLinkWithAddedParams(_ref3) {
  var clientLocation = _ref3.clientLocation,
      link = _ref3.link,
      rank = _ref3.rank,
      type = _ref3.type,
      tabTitle = _ref3.tabTitle; // check if rank is number -- add 1 to make rank non zero based

  if (typeof rank === 'number') {
    rank++;
  }

  if (link !== null && link !== undefined && link.includes('businesscom.go2cloud.org')) {
    var aff_sub1 = 'bestpick|no';

    if (tabTitle !== undefined && tabTitle !== 'undefined') {
      aff_sub1 = "bestpick|".concat(tabTitle.replace(/\s+/g, '-').toLowerCase());
    }

    var encodedCurrentUrl = encodeURIComponent(clientLocation || (isBrowser() ? window.location.href : ''));
    var additionalQueryStringParams = QueryString.stringify({
      aff_sub: aff_sub1,
      aff_sub2: encodedCurrentUrl,
      // falling back to document.referrer (which should always be the page serving the iframe widget)
      aff_sub5: "pos|bbc".concat(rank, "|").concat(type)
    }, {
      encode: false,
      indices: false
    });
    return "".concat(link, "&").concat(additionalQueryStringParams);
  } else {
    return link;
  }
}
/**
 * Function that checks monetization data based on the pricing.selected type
 * returns affiliate link if pricing.selected type is valid
 * returns null if pricing.selected type is invalid
 *
 * @param {Object} pricing :: object containing data that sets monetization buttons
 */

export function generateAffiliateLink(pricing) {
  var affiliateLink = null;
  var affiliateTypes = ['affiliate', 'affiliate-widget', 'widget-affiliate'];

  if (pricing && affiliateTypes.includes(pricing.selected)) {
    var _pricing$affiliate;

    affiliateLink = (pricing === null || pricing === void 0 ? void 0 : (_pricing$affiliate = pricing.affiliate) === null || _pricing$affiliate === void 0 ? void 0 : _pricing$affiliate.link) || null;
  }

  return affiliateLink;
}
/**
 * Strip out html code from string
 */

export function stripOutHtml(string) {
  if (string === null || string === '') {
    return string;
  } else {
    return string.replace(/<[^>]*>/g, '');
  }
}
/**
 * Function that groups content by their displayModified field
 * returns an object with `YYYY-MM-DD` as keys and arrays of content as values
 *
 * @param {Array} data :: array containing data that contains displayModified field we use to group content together by
 */

export function groupContentByDate(data) {
  return data.reduce(function (acc, day) {
    // Get the year-month-day
    var date = day.displayModified.split('T')[0];

    if (!acc[date]) {
      acc[date] = [];
    }

    acc[date].push(day);
    return acc;
  }, {});
}
/**
 * Checks if app is BND.
 */

export function isBnd() {
  return process.env.GATSBY_SITE_HOST.includes('businessnewsdaily');
}